import { useQuery } from '@tanstack/react-query'
import { taskMonitorApi, type TaskSystemMonitor } from '@/lib/api/task-monitor'

// ── Query Key ─────────────────────────────────────────────────────────

export const taskSystemKeys = {
  all: ['task-system'] as const,
}

// ── Polling backoff ──────────────────────────────────────────────────
// While any pipeline or task is active, poll frequently.
//   <15s elapsed: 3s   <60s: 5s   else: 10s
function pollIntervalForElapsed(elapsedMs: number): number {
  if (elapsedMs < 15_000) return 3000
  if (elapsedMs < 60_000) return 5000
  return 10_000
}

function activeTaskInterval(data: TaskSystemMonitor | undefined): number | false {
  if (!data) return 5000
  const hasActive =
    data.pipelines.items?.some((p) => ['pending', 'running'].includes(p.status)) ||
    data.creation_tasks.items?.some((t) => ['pending', 'queued', 'running'].includes(t.status))
  if (!hasActive) return false
  // Use the most recent updated_at across all items
  const allItems = [
    ...(data.pipelines.items ?? []),
    ...(data.creation_tasks.items ?? []),
  ]
  const latest = allItems.reduce((max, item) => {
    const t = new Date(item.updated_at).getTime()
    return t > max ? t : max
  }, 0)
  if (latest === 0) return 5000
  return pollIntervalForElapsed(Date.now() - latest)
}

// ── Shared hook — single source of truth ─────────────────────────────

export function useTaskSystem() {
  return useQuery<TaskSystemMonitor>({
    queryKey: taskSystemKeys.all,
    queryFn: () => taskMonitorApi.getTaskSystemStatus(),
    refetchInterval: (query) => activeTaskInterval(query.state.data),
    staleTime: 10_000,
    refetchIntervalInBackground: false,
  })
}